import { ReactElement, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Button, Typography, useThemeValue } from '@altermaind/web/ui';
import { Routes } from '@/_shared';
import { RenderLocalesWithComponents, VerticalStepper } from '@/_shared/components';
import { convertSizeToNumber } from '@/_shared/helpers';
import { useNotification } from '@/_shared/hooks/useNotification';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { INTERNAL_STEPPER } from '@/configuration/constants';
import { getUserProfileCall } from '@/core/state/api/getUserProfileCall';
import { getDownloadEmployeesImportsTemplateCall } from '@/people/state/api';

type Props = {
  setStepper: (step: number) => void;
};

type Step = {
  title: string;
  description: ReactElement | string;
};

export const StartConfiguration = ({ setStepper }: Props) => {
  const t = useTranslations('configuration.hr.start');
  const tShared = useTranslations();
  const { notify } = useNotification();

  const { genericResponsiveValue } = useResponsiveStyle();
  const navigate = useNavigate();

  const { firstName, skipConfig } = getUserProfileCall.hook();
  const {
    loading: loadingGetDownloadEmployeesImportsTemplate,
    execute: executeGetDownloadEmployeesImportsTemplate,
  } = getDownloadEmployeesImportsTemplateCall.hook();

  const gapXXXL = convertSizeToNumber(`${useThemeValue('sizes', 'xxxl')}`) || 64;
  const gapL = convertSizeToNumber(`${useThemeValue('sizes', 'l')}`) || 40;

  const handleDownload = useCallback(() => {
    executeGetDownloadEmployeesImportsTemplate({
      errorSaga: () => {
        notify(tShared('errors.genericApiError'), 'error');
      },
    });
  }, [executeGetDownloadEmployeesImportsTemplate, notify, tShared]);

  const steps: Step[] = [
    {
      title: t('setup.step1.title'),
      description: t('setup.step1.description'),
    },
    {
      title: t('setup.step2.title'),
      description: t('setup.step2.description'),
    },
    {
      title: t('setup.step3.title'),
      description: (
        <RenderLocalesWithComponents
          text={t('setup.step3.description', {
            downloadFile: '!{DOWNLOAD_FILE}!',
          })}
          placeholders={{
            '!{DOWNLOAD_FILE}!': (
              <Typography
                onClick={loadingGetDownloadEmployeesImportsTemplate ? undefined : handleDownload}
                as='span'
                variant='p2'
                style={{ textDecoration: 'underline', cursor: 'pointer', color: 'inherit' }}
              >
                {t('setup.step3.downloadFileLabel')}
              </Typography>
            ),
          }}
        />
      ),
    },
  ];

  const skipConfiguration = useCallback(() => {
    skipConfig({});
    navigate(Routes.Homepage);
  }, [navigate, skipConfig]);

  return (
    <Box
      display='flex'
      flexDirection={genericResponsiveValue({
        breakPoints: { desktop: 'row' },
        defaultValue: 'column',
      })}
      alignItems='flex-start'
      justifyContent={genericResponsiveValue({
        breakPoints: { desktop: 'center' },
        defaultValue: 'left',
      })}
      px={genericResponsiveValue({
        breakPoints: { desktop: 'xl' },
        defaultValue: 'xs',
      })}
      py={genericResponsiveValue({
        breakPoints: { desktop: 'xl' },
        defaultValue: 'none',
      })}
      width='100%'
      style={{ gap: gapXXXL }}
      flex={10}
    >
      <Box
        flex={genericResponsiveValue({
          breakPoints: { desktop: 4 },
          defaultValue: 10,
        })}
      >
        <Typography
          variant='h2'
          color='primary/text'
          fontSize={genericResponsiveValue({
            breakPoints: { desktop: 50 as never },
            defaultValue: 'xxxl',
          })}
          lineHeight={genericResponsiveValue({
            breakPoints: { desktop: '60px' as never },
            defaultValue: 'xxxl',
          })}
          fontWeight='bold'
          mb={genericResponsiveValue({
            breakPoints: { desktop: 'm' },
            defaultValue: 'xs',
          })}
          mt={genericResponsiveValue({
            breakPoints: { desktop: 'none' },
            defaultValue: 'xl',
          })}
        >
          {t('title', { name: firstName })}
        </Typography>
        <Typography variant='h4' fontWeight={200} mb='xl' color='primary/text'>
          {t('subtitle')}
        </Typography>
        <Box
          display='flex'
          alignItems={genericResponsiveValue({
            breakPoints: { desktop: 'center' },
            defaultValue: 'flex-start',
          })}
          justifyContent={genericResponsiveValue({
            breakPoints: { desktop: 'flex-start' },
            defaultValue: 'left',
          })}
          style={{ gap: gapL }}
        >
          <Button
            data-testid='admin-start-configuration-button-next'
            spinnerColor='white'
            variant='primary'
            onClick={() => setStepper(INTERNAL_STEPPER.CONFIGURATION)}
          >
            {t('startConfiguration')}
          </Button>
          <Button
            data-testid='admin-start-configuration-button-skip'
            spinnerColor='white'
            onClick={skipConfiguration}
            variant='link/primary'
            style={{ textDecoration: 'none', cursor: 'pointer' }}
          >
            {t('skipConfiguration')}
          </Button>
        </Box>
      </Box>
      <Box
        flex={genericResponsiveValue({
          breakPoints: { desktop: 6 },
          defaultValue: 10,
        })}
      >
        <VerticalStepper title={t('verticalStepTitle')} steps={steps} />
      </Box>
    </Box>
  );
};
